import Vue from 'vue';
import Router, { RawLocation, Route } from 'vue-router';
import ApplicationShell from '@/views/applicationShell/ApplicationShell.vue';
import store from '@/store';
import NotFound from '@/views/notFound/NotFound.vue';
import { allUserRoles, UserRoleEnum } from '@/enums/userRolesEnum';

const originalPush = Router.prototype.push;
// @ts-ignore
Router.prototype.push = function push(location, onResolve, onReject) {
  // will handle all routes unless duplicate, then will continue to the next if condition
    if (onResolve || onReject) {
      return originalPush.call(this, location, onResolve, onReject)
  }

  if (location && typeof (location) === 'string' && location.trim) {
      location = location.trim();
  }

  // void if duplicate navigation
  if (window && window.location) {
      const {pathname, search} = window.location;
      if (pathname === ((location || '') as any).to || `${pathname}${search}` === location) {
          return;
      }
  }

  // @ts-ignore
  const {name, params = {}} = this.history && this.history.current ? this.history.current : {};
  const newParams = (location as any).params || {};
  const hasNewValue = Object.keys(newParams).some(key => String(params[key]) !== String(newParams[key]));
  if (name === (location as any).name && Object.keys(params).length === Object.keys(newParams).length && !hasNewValue) {
    return;
  }

  return originalPush.call<Router, [RawLocation], Promise<Route>>(this, location).catch((err: any) => {
   // should never get here, let's add a console log incase it does. I don't think this is major enough to throw an exception
    console.log('Error:', err);
  });
}

Vue.use(Router);

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        {
            path: '/login',
            name: 'login',
            component: () => import(/* webpackChunkName: "login" */ '@/views/login/Login.vue'),
            meta: { public: true }
        },
        {
            path: '/public/community-calendar/:eventId',
            name: 'publicCommunityCalendarEvent',
            component: () => import(/* webpackChunkName: "communityCalendar" */ '@/views/communityCalendar/communityCalendarEvent/CommunityCalendarEventDetails.vue'),
            props: true,
            meta: { public: true }
        },
        {
            path: '/',
            props: true,
            component: ApplicationShell,
            children: [
                {
                    path: '/',
                    name: 'home',
                    component: () => import(/* webpackChunkName: "home" */ '@/views/home/Home.vue'),
                    meta: { roles: allUserRoles }
                },
                {
                    path: '/dvd/dvds',
                    name: 'dvds',
                    component: () => import(/* webpackChunkName: "dvdCrud" */ '@/dvd-orders/crud/DvdList.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER ] }
                },
                {
                    path: '/dvd/dvds/:dvdId',
                    name: 'dvd',
                    component: () => import(/* webpackChunkName: "dvdCrud" */ '@/dvd-orders/crud/Dvd.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER ] }
                },
                {
                    path: '/dvd/dvd-orders',
                    name: 'dvdOrderList',
                    component: () => import(/* webpackChunkName: "dvds" */ '@/dvd-orders/DvdOrderList.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT ] }
                },
                {
                    path: '/dvd/dvd-orders/:orderId',
                    name: 'dvdOrderDetails',
                    component: () => import(/* webpackChunkName: "dvds" */ '@/dvd-orders/DvdOrderDetails.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT ] },
                    props: true
                },
                {
                    path: '/support-forms/support-form',
                    name: 'supportForms',
                    component: () => import(/* webpackChunkName: "supportForm" */ '@/support-forms/list/SupportForms.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.WEATHER ] },
                },
                {
                    path: '/support-forms/support-form/types/:typeId',
                    name: 'supportFormByType',
                    component: () => import(/* webpackChunkName: "supportForm" */ '@/support-forms/types/SupportFormByType.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.WEATHER ] },
                    props: true
                },
                {
                    path: '/support-forms/support-form/:formId',
                    name: 'supportFormDetails',
                    component: () => import(/* webpackChunkName: "supportForm" */ '@/support-forms/details/SupportForm.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.WEATHER ] },
                    props: true
                },
                {
                    path: '/public-site/member-alerts',
                    name: 'alertSearch',
                    component: () => import(/* webpackChunkName: "memberAlertSearch" */ '@/member-alerts/MemberAlertSearch.vue'),
                    meta: { roles: allUserRoles },
                },
                {
                    path: '/public-site/member-alerts/:alertId',
                    name: 'alert',
                    component: () => import(/* webpackChunkName: "memberAlertSearch" */ '@/member-alerts/MemberAlert.vue'),
                    meta: { roles: allUserRoles },
                    props: true
                },
                {
                    path: '/events/scheduled-events',
                    name: 'scheduledEvents',
                    component: () => import(/* webpackChunkName: "scheduledEvents" */ '@/views/sportsSchedules/Events.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT ] },
                },
                {
                    path: '/events/generic-content',
                    name: 'genericContent',
                    component: () => import(/* webpackChunkName: "scheduledEvents" */ '@/generic-content/GenericContent.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN] },
                },
                {
                    path: '/events/generic-content/:genericContentId',
                    name: 'genericContentDetail',
                    component: () => import(/* webpackChunkName: "scheduledEvents" */ '@/generic-content/GenericContentDetail.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN] },
                    props: true
                },
                {
                    path: '/events/scheduled-events/calendar/:year?/:month?',
                    name: 'scheduledEventsCalendar',
                    component: () => import(/* webpackChunkName: "scheduledEvents" */ '@/views/sportsSchedules/Events.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT ] },
                    props: true
                },
                {
                    path: '/events/scheduled-events/:eventType?/:eventId',
                    name: 'scheduledEvent',
                    component: () => import(/* webpackChunkName: "scheduledEvents" */ '@/views/sportsSchedules/sportsSchedule/EventDetail.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT ] },
                    props: true
                },
                {
                    path: '/support-forms/community-calendar',
                    name: 'communityCalendarSchedules',
                    component: () => import(/* webpackChunkName: "communityCalendar" */ '@/views/communityCalendar/CommunityCalendar.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.WEATHER ] },
                },
                {
                    path: '/support-forms/community-calendar/calendar/:year?/:month?',
                    name: 'communityCalendar',
                    component: () => import(/* webpackChunkName: "communityCalendar" */ '@/views/communityCalendar/CommunityCalendar.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.WEATHER ] },
                    props: true
                },
                {
                    path: '/support-forms/community-calendar/pending-events',
                    name: 'communityCalendarPendingEvents',
                    component: () => import(/* webpackChunkName: "communityCalendar" */ '@/views/communityCalendar/CommunityCalendar.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.WEATHER ] },
                },
                {
                    path: '/support-forms/community-calendar/:eventId',
                    name: 'communityCalendarEvent',
                    component: () => import(/* webpackChunkName: "communityCalendar" */ '@/views/communityCalendar/communityCalendarEvent/CommunityCalendarEventDetails.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.WEATHER ] },
                    props: true
                },
                {
                    path: '/events/teams',
                    name: 'teams',
                    component: () => import(/* webpackChunkName: "team" */ '@/team/Teams.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] }
                },
                {
                    path: '/events/teams/:teamId',
                    name: 'teamDetail',
                    component: () => import(/* webpackChunkName: "team" */ '@/team/TeamDetail.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: '/events/providers',
                    name: 'providers',
                    component: () => import('@/providers/providers.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] }
                },
                {
                    path: '/events/providers/:channelId',
                    name: 'providerDetail',
                    component: () => import('@/providers/providerDetail.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] }
                },
                // {
                //     path: '/events/channels',
                //     name: 'channels',
                //     component: () => import('@/channels/channels.vue'),
                //     props: true,
                //     meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] }
                // },
                {
                    path: '/support-forms/email-logs',
                    name: 'emailLogs',
                    component: () => import(/* webpackChunkName: "communityCalendar" */ '@/email/EmailLogs.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN ] },
                    props: true
                },
                {
                    path: '/events/streams',
                    name: 'streams',
                    component: () => import(/* webpackChunkName: "stream" */ '@/stream/Streams.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] }
                },
                {
                    path: '/events/streams/:streamId',
                    name: 'streamDetail',
                    component: () => import(/* webpackChunkName: "stream" */ '@/stream/StreamDetail.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.WEATHER ] }
                },
                {
                    path: '/events/news-shows',
                    name: 'newsShows',
                    component: () => import(/* webpackChunkName: "newsShows" */ '@/news-show/NewsShows.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] }
                },
                {
                    path: '/events/news-shows/:newsShowId',
                    name: 'newsShowDetail',
                    component: () => import(/* webpackChunkName: "newsShows" */ '@/news-show/NewsShowDetail.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] },
                },
                {
                    path: '/support-forms/forms',
                    name: 'forms',
                    component: () => import(/* webpackChunkName: "form" */ '@/custom-forms/admin/Forms.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: '/support-forms/forms/:formId',
                    name: 'formDetail',
                    component: () => import(/* webpackChunkName: "form" */ '@/custom-forms/admin/FormDetail.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {

                    path: '/support-forms/form-submissions',
                    name: 'formSubmissions',
                    component: () => import(/* webpackChunkName: "formSubmission" */ '@/custom-forms/FormSubmissions.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER ] }
                },
                {
                    path: '/support-forms/form-submissions/:formSubmissionId',
                    name: 'formSubmissionDetail',
                    component: () => import(/* webpackChunkName: "formSubmission" */ '@/custom-forms/FormSubmissionDetail.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.VIEWER ] }
                },
                {
                    path: '/public-site/website-cms',
                    name: 'websiteCMS',
                    component: () => import(/* webpackChunkName: "websiteCMS" */ '@/views/websiteCMS/WebsiteCMS.vue'),
                    meta: { roles: allUserRoles }
                },
                {
                    path: '/settings',
                    name: 'settings',
                    component: () => import(/* webpackChunkName: "settings" */ '@/views/settings/Settings.vue'),
                    meta: { roles: allUserRoles }
                },
                {
                    path: '/reports/service-area-reports',
                    name: 'serviceAreaReports',
                    component: () => import(/* webpackChunkName: "settings" */ '@/service-address-reports/ServiceAreaReports.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: '/reports/service-address-reports',
                    name: 'serviceAddressReports',
                    component: () => import(/* webpackChunkName: "settings" */ '@/service-address-reports/ServiceAddressReports.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: '/products-and-services/display-details',
                    name: 'serviceTypes',
                    component: () => import(/* webpackChunkName: "serviceTypes" */ '@/products-and-services/display-details/ServiceTypes.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: '/products-and-services/display-details/service-type/:serviceTypeId/:businessResidence',
                    name: 'serviceTypeDetail',
                    component: () => import(/* webpackChunkName: "serviceTypes" */ '@/products-and-services/display-details/ServiceTypeDetail.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: '/products-and-services/display-details/service/:serviceId',
                    name: 'serviceDetail',
                    component: () => import(/* webpackChunkName: "serviceDetail" */ '@/products-and-services/display-details/ServiceDetail.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: '/products-and-services/display-details/service/:serviceId/product/:productId',
                    name: 'productDetail',
                    component: () => import(/* webpackChunkName: "productDetail" */ '@/products-and-services/display-details/ProductDetail.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: 'products-and-services/cross-sales',
                    name: 'crossSales',
                    component: () => import(/* webpackChunkName: "crossSales" */ '@/views/productsAndServices/crossSales/CrossSalesView.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: 'products-and-services/digital-tv-packages',
                    name: 'digitalTVPackages',
                    component: () => import(/* webpackChunkName: "digitalTVPackages" */ '@/views/productsAndServices/digital-tv-packages/DigitalTVPackages.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: 'products-and-services/cat-ids',
                    name: 'pnsCatIds',
                    component: () => import(/* webpackChunkName: "pnsCatIds" */ '@/views/productsAndServices/CatIds.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: 'products-and-services/terms',
                    name: 'terms',
                    component: () => import(/* webpackChunkName: "pnsCatIds" */ '@/views/productsAndServices/terms/Terms.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: 'products-and-services/discounts',
                    name: 'discounts',
                    component: () => import(/* webpackChunkName: "discounts" */ '@/views/productsAndServices/Discounts.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: 'products-and-services/discounts/:discountId',
                    name: 'discountDetails',
                    component: () => import(/* webpackChunkName: "discounts" */ '@/views/productsAndServices/DiscountDetails.vue'),
                    props: true,
                    meta: { roles: [ UserRoleEnum.ADMIN ] }
                },
                {
                    path: '/shopping-carts/carts',
                    name: 'shoppingCarts',
                    component: () => import(/* webpackChunkName: "shoppingCarts" */ '@/views/productsAndServices/shoppingCarts/list/ShoppingCarts.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT ] }
                },
                {
                    path: '/shopping-carts/carts/:cartNumber',
                    name: 'shoppingCartDetail',
                    component: () => import(/* webpackChunkName: "shoppingCartSearch" */ '@/views/productsAndServices/ShoppingCartDetail.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT ] },
                    props: true
                },
                {
                    path: '/weather/live-cameras',
                    name: 'liveCameras',
                    component: () => import(/* webpackChunkName: "weather" */ '@/weather/live-cameras/LiveCameras.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] }
                },
                {
                    path: '/weather/live-cameras/:cameraId',
                    name: 'liveCameraDetail',
                    component: () => import(/* webpackChunkName: "weather" */ '@/weather/live-cameras/LiveCameraDetail.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] },
                    props: true
                },
                {
                    path: '/weather/maps',
                    name: 'weatherMaps',
                    component: () => import(/* webpackChunkName: "weather" */ '@/weather/maps/Maps.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] }
                },
                {
                    path: '/weather/maps/:mapId',
                    name: 'weatherMapDetail',
                    component: () => import(/* webpackChunkName: "weather" */ '@/weather/maps/MapDetail.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] },
                    props: true
                },
                {
                    path: '/weather/news-stories',
                    name: 'weatherStories',
                    component: () => import(/* webpackChunkName: "weather" */ '@/weather/weather-story/WeatherStories.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] }
                },
                {
                    path: '/weather/news-stories/:storyId',
                    name: 'weatherStoryDetail',
                    component: () => import(/* webpackChunkName: "weather" */ '@/weather/weather-story/WeatherStoryDetail.vue'),
                    meta: { roles: [ UserRoleEnum.ADMIN, UserRoleEnum.CUSTOMER_EXPERIENCE_AGENT, UserRoleEnum.VIEWER, UserRoleEnum.WEATHER ] },
                    props: true
                },
                /* Not Found lives inside application shell to ensure they must be logged in.
                 * Users who aren't logged in will be redirected to the login page
                 */
                {
                    path: '*',
                    name: 'notFound',
                    component: NotFound
                }
            ]
        }
    ],
});

router.beforeEach(async (to, from, next) => {
    // Set the last "from" route in vuex
    // This can be used on login to re-navigate to the user's last view.
    store.commit('user/setLastRoute', from.fullPath);

    if (to.matched.some(record => record.meta.public)) {
        next();
    } else {
        if (!await store.dispatch('user/isLoggedIn')) {
            next({ name: 'login', query: {redirect: to.fullPath} });
        } else {
            if (to && to.meta && to.meta.roles && !store.getters['user/hasRole'](to.meta.roles)) {
                (Vue as any).notify({
                    title: 'Unauthorized',
                    text: 'You are not authorized to view this screen',
                    type: 'error',
                });
                return next({ name: 'home' });
            }
            next();
        }
    }
});

export default router;
